import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { ByRegistered } from "../../../../model/filters/by-registered";
import { SingleChoice } from "../../../../model/singlechoice";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { BehaviorSubject, Observer } from "rxjs";
import { UsersService } from "../../../../services/users.service";
import { FilterService } from "../../../../services/filter.service";
import { NavigationHelper } from "../../../../services/navigation.helper";
import { Router } from "@angular/router";
import { Navigation } from "../../../../model/navigation";
import { Labeler } from "../../../../model/labeler";
import { NotComponent } from "../not/not.component";
import { Filter } from "../../../../model/filters/filter";
import { MatchAny } from "../../../../model/filters/match.any";

@Component({
  selector: "by-registered-form",

  templateUrl: "./by-registered-form.component.html",
  styleUrl: "./by-registered-form.component.css",
})
export class ByRegisteredFormComponent {
  @Input() filter: ByRegistered = new ByRegistered();
  @Output() applyFilter = new EventEmitter<ByRegistered>();
  @Output() choiceEvent: EventEmitter<SingleChoice> = new EventEmitter();
  byRegisteredForm: FormGroup;

  registeredLabeler = new BehaviorSubject<string>("-");
  completionUrl = new BehaviorSubject<string>(
    "tickets/-1/filter/by.registered"
  );
  currentId: number | null;
  @ViewChild(NotComponent) notComponent!: NotComponent;
  byRegisteredFilter: ByRegistered = new ByRegistered();
  editingIndex: number | null = null;

  constructor(
    private fb: FormBuilder,
    private users: UsersService,
    private nav: NavigationHelper,
    private filterService: FilterService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const existingFilters =
      this.filter instanceof MatchAny ? this.filter.filters : [this.filter];

    const userIdsFromFilter: number[] = existingFilters
      .filter((f): f is ByRegistered => f instanceof ByRegistered)
      .map((f) => f.id)
      .filter((id) => id !== undefined) as number[];

    const storedUserIds = localStorage.getItem("registeredUserIds");
    const userIdsFromStorage = storedUserIds
      ? (JSON.parse(storedUserIds) as number[])
      : [];

    const mergedIds = Array.from(
      new Set([...userIdsFromFilter, ...userIdsFromStorage])
    );

    if (mergedIds.length > 0) {
      this.initializeForm(mergedIds);
      this.currentId = mergedIds[0];
      this.registeredLabeler.next(this.users.labelForId(this.currentId));
    } else {
      this.byRegisteredForm = this.fb.group({
        registeredUsers: this.fb.array([
          this.fb.control(null, Validators.required),
        ]),
      });
    }
  }

  restoreRegisteredUsers(userIds: (number | null)[]): void {
    this.byRegisteredForm.setControl("registeredUsers", this.fb.array([]));

    for (const id of userIds) {
      this.registeredUsers.push(this.fb.control(id, Validators.required));
    }
  }

  initializeForm(userIds: number[]): void {
    const controls = userIds.map((id) =>
      this.fb.control(id, Validators.required)
    );
    if (controls.length === 0)
      controls.push(this.fb.control(null, Validators.required));

    this.byRegisteredForm = this.fb.group({
      registeredUsers: this.fb.array(controls),
    });

    this.currentId = controls[0].value ?? null;
    if (this.currentId !== null) {
      this.registeredLabeler.next(this.users.labelForId(this.currentId));
    }
  }

  onFilterChanged(finalFilter: Filter) {
    this.applyFilter.emit(finalFilter);
  
  }
  apply(): void {
    const ids: number[] = this.registeredUsers.value.filter(
      (id: number | null | undefined): id is number =>
        id !== null && id !== undefined
    );

    if (ids.length === 0) return;

    localStorage.setItem("registeredUserIds", JSON.stringify(ids));

    let finalFilter: Filter;

    if (ids.length === 1) {
      finalFilter = new ByRegistered(ids[0]);
    } else {
      finalFilter = new MatchAny(ids.map((id) => new ByRegistered(id)));
    }

    this.notComponent.filter = finalFilter;
    const modifiedFilter: Filter = this.notComponent.getModifiedFilter();

    this.applyFilter.emit(modifiedFilter);

  }

  relaySingleChoice(choice: SingleChoice) {
    this.nav.startChoice(choice);
  }
  onRegisteredChoice(registered: number): void {
    if (
      this.editingIndex !== null &&
      this.registeredUsers.at(this.editingIndex)
    ) {
      this.registeredUsers.at(this.editingIndex).setValue(registered);
    } else if (this.registeredUsers.length > 0) {
      this.registeredUsers.at(0).setValue(registered);
    }

    const currentValues = this.registeredUsers.value.filter(
      (id) => id !== null
    );
    localStorage.setItem("registeredUserIds", JSON.stringify(currentValues));

    this.currentId = registered;

    this.router
      .navigate(["tickets/-1/filter/by.registered/" + registered])
      .then(() => {
        this.nav.freshNavigation;
      });
  }

  get registeredUsers(): FormArray<FormControl> {
    return this.byRegisteredForm.get(
      "registeredUsers"
    ) as FormArray<FormControl>;
  }

  addUserField(): void {
    this.registeredUsers.push(this.fb.control(null, Validators.required));

    const currentValues = this.registeredUsers.value.filter(
      (id) => id !== null
    );
    localStorage.setItem("registeredUserIds", JSON.stringify(currentValues));
  }

  removeField(index: number): void {
    if (this.registeredUsers.length > 1) {
      this.registeredUsers.removeAt(index);
      const currentValues = this.registeredUsers.value.filter((id) => id !== null);
      localStorage.setItem("registeredUserIds", JSON.stringify(currentValues));
    }
  }

  get registeredObserver(): Partial<Observer<number>> {
    return {
      next: (id) => {
        this.onRegisteredChoice(id);
      },
    };
  }
  get fakeId(): number {
    return Navigation.NEW;
  }
  get registeredLabelerByService(): Labeler {
    return this.users;
  }
  setEditingIndex(index: number): void {
    this.editingIndex = index;
    localStorage.setItem("editingUserIndex", index.toString());
  }
}
